import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import api from "../../lib/api";
import { toast } from "sonner";
import { Bell, BellOff, RefreshCw, AlertTriangle, AlertOctagon, Info, CheckCircle2 } from "lucide-react";

const SEV = {
  critical: { icon: AlertOctagon, cls: "bg-rose-50 text-rose-700 border-rose-200" },
  warn:     { icon: AlertTriangle, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  info:     { icon: Info,         cls: "bg-sky-50 text-sky-700 border-sky-200" },
};

export default function AdminAlerts() {
  const [data, setData] = useState({ alerts: [], open_count: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("open");
  const [evalBusy, setEvalBusy] = useState(false);

  const load = (status = tab) => {
    setLoading(true);
    api.get(`/admin/alerts?status=${status}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load alerts"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const evaluate = async () => {
    setEvalBusy(true);
    try {
      const r = await api.post("/admin/alerts/evaluate");
      toast.success(`Evaluated · ${r.data.count} new alert(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Evaluation failed"); }
    finally { setEvalBusy(false); }
  };

  const dismiss = async (id) => {
    try {
      await api.post(`/admin/alerts/${id}/dismiss`);
      toast.success("Dismissed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <AdminLayout
      title="Operational alerts"
      subtitle="Float, batch, send-blocked and SLA breach signals. Critical signals are pushed; staff dismiss when handled."
      actions={
        <div className="flex gap-2">
          <Button onClick={() => load()} variant="outline" size="sm" className="rounded-xl gap-1.5" data-testid="alerts-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
          <Button onClick={evaluate} disabled={evalBusy} size="sm"
                  className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                  data-testid="alerts-eval-btn">
            <Bell className="w-3.5 h-3.5" /> Run evaluator
          </Button>
        </div>
      }
    >
      <Tabs value={tab} onValueChange={setTab} className="w-full mb-4">
        <TabsList className="bg-muted/40 rounded-xl" data-testid="alerts-tabs">
          <TabsTrigger value="open" data-testid="tab-open">
            Open <Badge variant="outline" className="ml-2 text-[10px] tabular-nums">{data.open_count}</Badge>
          </TabsTrigger>
          <TabsTrigger value="dismissed" data-testid="tab-dismissed">Dismissed</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="xch-card overflow-hidden" data-testid="alerts-list">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : data.alerts.length === 0 ? (
          <div className="p-12 text-center" data-testid="alerts-empty">
            <BellOff className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <div className="text-sm text-muted-foreground">
              {tab === "open" ? "No open alerts. Run the evaluator to scan." : "Nothing here."}
            </div>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {data.alerts.map((a) => {
              const sev = SEV[a.severity] || SEV.info;
              const Icon = sev.icon;
              return (
                <div key={a.id} className="p-4 flex items-start gap-3" data-testid={`alert-${a.id}`}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${sev.cls} border`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-foreground flex items-center gap-2 flex-wrap">
                      {a.title}
                      <Badge variant="outline" className={`text-[9px] uppercase font-bold tracking-wider ${sev.cls}`}>{a.severity}</Badge>
                      <Badge variant="outline" className="text-[9px] font-mono uppercase">{a.type}</Badge>
                      {a.status === "dismissed" && (
                        <Badge variant="outline" className="text-[9px] uppercase font-bold bg-slate-100 text-slate-600 border-slate-200">dismissed</Badge>
                      )}
                    </div>
                    {a.body && <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{a.body}</div>}
                    <div className="text-[10px] text-muted-foreground tabular-nums mt-1">
                      {new Date(a.ts).toLocaleString("en-IE")}
                      {a.order_id && <span className="ml-2 font-mono">order {a.order_id.slice(0, 8)}…</span>}
                    </div>
                  </div>
                  {a.status === "open" && (
                    <Button onClick={() => dismiss(a.id)} size="sm" variant="outline"
                            className="rounded-lg gap-1 shrink-0"
                            data-testid={`alert-dismiss-${a.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Dismiss
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
